import { z } from "zod";
import type { ApiMoney } from "./tasks";

/**
 * Herkese açık web sitesi uçları (K-122): yerleşim yerleri, hizmet veren listesi ve landing sayfa verisi.
 * Kayıt gerekmez; fiyatlar müşterinin göreceği gibi komisyon dahil (K-012).
 */

const Locale = z
  .string()
  .regex(/^[a-z]{2}$/)
  .optional();
const Code = z.string().regex(/^[a-z0-9_-]{1,64}$/);

export const SiteLocaleQuery = z.object({ locale: Locale });

/** GET /v1/markets/:market/places */
export interface PlacesResponse {
  locale: string;
  regions: {
    code: string;
    name: string;
    localities: {
      /** iş adresinde `localityId` (rezervasyon formu) */
      id: string;
      code: string;
      name: string;
      slug: string;
      latitude: number | null;
      longitude: number | null;
    }[];
  }[];
}

/** GET /v1/markets/:market/providers — kategori, hizmet ve/veya yer kapsamında herkese açık liste */
export const SiteProvidersQuery = z.object({
  locale: Locale,
  category: Code.optional(),
  service: Code.optional(),
  locality: Code.optional(),
  sort: z
    .enum(["recommended", "rating", "price_asc", "price_desc", "most_tasks"])
    .default("recommended"),
  minRating: z.coerce.number().min(1).max(5).optional(),
  language: z
    .string()
    .regex(/^[a-z]{2}$/)
    .optional(),
  type: z.enum(["individual", "company"]).optional(),
  /** seçilen başlangıç (rezervasyon): verilirse her hizmet veren için `available` hesaplanır */
  at: z.string().datetime({ offset: true }).optional(),
  /** işin tahmini süresi (dk); `at` ile birlikte */
  minutes: z.coerce.number().int().min(30).max(720).optional(),
  /** yalnızca o saatte müsait olanlar */
  available: z.enum(["true", "false"]).optional(),
  limit: z.coerce.number().int().min(1).max(48).default(24),
  offset: z.coerce.number().int().min(0).max(1000).default(0),
});
export type SiteProvidersQuery = z.infer<typeof SiteProvidersQuery>;

export interface SiteProvider {
  slug: string;
  displayName: string;
  type: "individual" | "company";
  rating: { average: number | null; count: number };
  completedTasks: number;
  isNew: boolean;
  /** Poji Verified (sabıka belgesi onaylı) rozeti var (K-168) */
  verified: boolean;
  languages: string[];
  /** hizmet seçiliyse o hizmetin fiyatı; değilse kapsamdaki en düşük ("…'den başlayan") */
  price: ApiMoney | null;
  priceUnit: "hour" | "fixed" | null;
  /** fiyat birden çok hizmetin en düşüğü mü */
  priceFrom: boolean;
  /** kapsamdaki hizmet adları (en fazla 3) */
  services: string[];
  /** aynı sırayla hizmet kodları (görsel ve bağlantı için) */
  serviceCodes: string[];
  /** herkese açık kovadaki görsel (istemci Storage adresiyle birleştirir) */
  avatar: { bucket: "avatars"; path: string } | null;
  /** hizmet verenin seçtiği kapak çizimi (`<tür>:<sıra>`); yoksa null */
  coverArt: string | null;
  /** hizmet verenin yüklediği kapak fotoğrafı; varsa çizimin önüne geçer (K-165) */
  coverImage: { bucket: "provider-media"; path: string } | null;
  /** `at` verildiyse o saatte müsait mi; verilmediyse null */
  available: boolean | null;
  /** hizmet seçiliyse hizmet verenin o hizmetteki en az süresi (dk) */
  minMinutes: number | null;
}

export interface SiteProvidersResponse {
  total: number;
  providers: SiteProvider[];
}

/** GET /v1/markets/:market/landing — P-12 veri blokları */
export const SiteLandingQuery = z.object({
  locale: Locale,
  category: Code.optional(),
  service: Code.optional(),
  locality: Code.optional(),
});

export interface SiteLandingReview {
  rating: number;
  comment: string;
  author: string;
  service: string | null;
  provider: { slug: string; displayName: string };
  createdAt: string;
}

export interface SiteLandingResponse {
  stats: {
    providerCount: number;
    reviewCount: number;
    ratingAverage: number | null;
    /** saatlik fiyat aralığı (komisyon dahil); saatlik hizmet yoksa null */
    hourly: { min: ApiMoney; median: ApiMoney; max: ApiMoney } | null;
  };
  /** P-12 ince içerik eşiği (veya admin override): false ise noindex + site haritası dışı */
  indexable: boolean;
  faqs: { question: string; answer: string }[];
  reviews: SiteLandingReview[];
}

/** GET /v1/markets/:market/services/:code/form — rezervasyon formu (K-036), çevirili */
export interface ServiceFormResponse {
  locale: string;
  service: {
    code: string;
    name: string;
    pricingType: "hourly" | "fixed";
    locationModes: "customer" | "provider" | "both";
    defaultMinMinutes: number | null;
    urgentEnabled: boolean;
  };
  questions: {
    code: string;
    kind: string;
    section: string | null;
    required: boolean;
    minValue: number | null;
    maxValue: number | null;
    maxLength: number | null;
    visibleIf: Record<string, unknown> | null;
    label: string;
    help: string | null;
    placeholder: string | null;
    options: {
      code: string;
      label: string;
      estimatedMinutes: number | null;
      acceptsText: boolean;
      routesTo: string | null;
    }[];
  }[];
}

/** GET /v1/markets/:market/providers/:slug/slots — bir gün için başlangıç saatleri (K-062) */
export const SlotsQuery = z.object({
  service: Code,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  minutes: z.coerce.number().int().min(30).max(720),
  locality: Code.optional(),
});

export interface SlotsResponse {
  timeZone: string;
  slots: { time: string; start: string; available: boolean }[];
}
